import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";

interface DialogSuggestionFriendState {
  users: any[];
  existingIds: Set<string>;
  appRecord: AppRecordJK;
  initializePage: (looking: string) => Promise<void>;
  fetchSuggestionFriends: (looking: string, searchText: string, isRefresh: boolean) => Promise<void>;
  onFollowingUser: (user: any) => Promise<void>;
  onUnFollowingUser: (user: any) => Promise<void>;
  onUninterestUser: (userId: string) => Promise<void>;
  disposePage: () => Promise<void>;
}

export const useDialogSuggestionFriendStore = create<DialogSuggestionFriendState>((set, get) => ({
  users: [],
  existingIds: new Set<string>(),
  appRecord: new AppRecordJK(),

  initializePage: async (looking: string) => {
    try {
      useDialogSuggestionFriendStore.getState().fetchSuggestionFriends(looking, "", true);
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchSuggestionFriends: async (looking: any, searchText: string, isRefresh: boolean) => {
    const { users, existingIds, appRecord } = get();

    let tmpAppRecord = new AppRecordJK({ ...appRecord });
    let tmpExistingIds = new Set(existingIds);
    let tmpUsers = [...users];

    try {
      let url = RouteApi.getSuggestionSearchMores;
      url += `token=${Service.token}`;

      if (isRefresh) {
        tmpAppRecord.loadingStatus = AppRecordStatusJK.loading;
        tmpExistingIds = new Set<string>();
        tmpUsers = [];
        set({
          appRecord: tmpAppRecord,
          users: tmpUsers,
          existingIds: tmpExistingIds,
        });
      } else {
        if (tmpAppRecord.canLoadMore()) {
          tmpAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
          set({ appRecord: tmpAppRecord });
        } else {
          return;
        }
      }

      const existingIdsArray: string[] = [...tmpExistingIds];
      const data = {
        looking_for_partner: looking,
        search_text: searchText,
        existing_ids: existingIdsArray,
      };

      const responses = await RequestApi(url, { data });
      if (responses.status === "success") {
        const newUsers: any[] = responses.records;

        if (newUsers && newUsers.length > 0) {
          tmpAppRecord.loadingStatus = AppRecordStatusJK.loaded;
        } else {
          tmpAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        }

        const filteredUsers = newUsers.filter((user) => !existingIds.has(`${user.id}`));

        filteredUsers.forEach((user) => tmpExistingIds.add(`${user.id}`));

        set({
          appRecord: tmpAppRecord,
          users: [...tmpUsers, ...newUsers],
          existingIds: tmpExistingIds,
        });
      } else {
        tmpAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ appRecord: tmpAppRecord });
      }
    } catch (error) {
      tmpAppRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
      set({ appRecord: tmpAppRecord });

      AppLog.show({
        message: `[fetchSuggestionFriends]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onUninterestUser: async (userId: string) => {
    try {
      const { users } = get();

      let url = RouteApi.submitUninterestUser;
      url += `token=${Service.token}`;
      url += `&user_id=${userId}`;

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const tmpUser = [...users];
        const deleteIndex = tmpUser.findIndex((user) => `${user.id}` === userId);

        if (deleteIndex !== -1) {
          tmpUser.splice(deleteIndex, 1);
          set({ users: tmpUser });
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onFollowingUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onFollowingUser: async (user: any) => {
    try {
      const { users } = get();
      let url = RouteApi.submitFollowingUser;
      url += `token=${Service.token}`;
      url += `&user_id=${user.id}`;

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const tmpUser = user;
        tmpUser.friend_status = "Following";

        const editIndex = users.findIndex((element) => element.id === tmpUser.id);

        if (editIndex !== -1) {
          set((state) => {
            const updatedUsers = [...state.users];
            updatedUsers[editIndex] = tmpUser;
            return { users: updatedUsers };
          });
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onFollowingUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onUnFollowingUser: async (user: any) => {
    try {
      const { users } = get();
      let url = RouteApi.submitUnFollowingUser;
      url += `token=${Service.token}`;
      url += `&user_id=${user.id}`;

      const responses = await RequestApi(url);
      if (responses.status === "success") {
        const tmpUser = user;
        tmpUser.friend_status = "None";

        const editIndex = users.findIndex((element) => element.id === tmpUser.id);

        if (editIndex !== -1) {
          set((state) => {
            const updatedUsers = [...state.users];
            updatedUsers[editIndex] = tmpUser;
            return { users: updatedUsers };
          });
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onFollowingUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  disposePage: async () => {
    try {
      set({
        users: [],
        existingIds: new Set<string>(),
        appRecord: new AppRecordJK(),
      });
    } catch (error) {
      AppLog.show({
        message: `[disposePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

